/**
 * Documents et transmission — PDF, Factur-X, Chorus Pro, relance, envoi de devis.
 *
 * Quatre Edge Functions étaient déployées, testées, et injoignables : aucun
 * écran ne les appelait. Ce domaine est le chaînon manquant. Il n'ajoute pas
 * de logique de génération — elle vit dans les fonctions — il ajoute ce qui
 * manquait pour pouvoir les atteindre sans contourner le socle : un niveau
 * d'action, un contrôle de rôle, un résumé confirmable et un journal.
 *
 * La cinquième, `send-quote`, n'existait pas du tout : l'application savait
 * produire un devis et s'arrêtait juste avant de le mettre dans les mains du
 * client.
 *
 * Le classement par niveau n'est pas cosmétique. Produire un PDF écrit dans le
 * Storage de l'entreprise et rien d'autre : c'est réversible, donc N2. Déposer
 * une facture sur Chorus Pro ou envoyer une relance à un client sort de
 * l'entreprise et ne se rattrape pas : c'est N3, donc confirmation obligatoire.
 *
 * La séquence e-facturation impose son propre ordre — PDF, puis XML CII, puis
 * dépôt. Les services ne le réimplémentent pas : les fonctions refusent déjà
 * l'étape suivante si la précédente manque, et dupliquer cette règle ici
 * l'aurait fait diverger.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import { round2 } from "../../money.js";

// ─── Entrées ───────────────────────────────────────────────────────────────

export const generateDocumentPdfInput = z.object({
  confirmationId: z.string().optional(),
  kind: z.enum(["quote", "invoice"]),
  id: z.string().uuid("Identifiant de document attendu"),
});

export const generateFacturxInput = z.object({
  confirmationId: z.string().optional(),
  invoiceId: z.string().uuid("Identifiant de facture attendu"),
  profile: z
    .enum(["minimum", "basicwl", "basic", "en16931", "extended"])
    .optional(),
});

export const submitChorusProInput = z.object({
  confirmationId: z.string().optional(),
  invoiceId: z.string().uuid("Identifiant de facture attendu"),
  /** Repris tel quel dans le résumé montré au patron avant confirmation. */
  invoiceNumber: z.string().trim().min(1),
  recipient: z.string().trim().min(1),
});

export const sendQuoteInput = z.object({
  confirmationId: z.string().optional(),
  quoteId: z.string().uuid("Identifiant de devis attendu"),
  /**
   * Numéro, destinataire et montant ne servent pas à l'envoi — la fonction
   * relit tout depuis la base. Ils servent au résumé montré au patron, et
   * c'est pour ça qu'ils entrent dans l'empreinte signée : ce qu'il a lu et
   * ce qui partira sont la même chose.
   */
  quoteNumber: z.string().trim().min(1),
  recipient: z.string().trim().min(1),
  recipientEmail: z.string().email("Adresse e-mail du client invalide"),
  amountTtc: z.number(),
  /** Mot d'accompagnement libre. Remplace la phrase par défaut. */
  message: z.string().trim().max(2000).optional(),
});

export const sendInvoiceInput = z.object({
  confirmationId: z.string().optional(),
  invoiceId: z.string().uuid("Identifiant de facture attendu"),
  /**
   * Comme pour le devis : ces champs ne servent pas à l'envoi, la fonction
   * relit tout depuis la base. Ils servent au résumé montré au patron, et
   * c'est pour ça qu'ils entrent dans l'empreinte signée.
   */
  invoiceNumber: z.string().trim().min(1),
  recipient: z.string().trim().min(1),
  recipientEmail: z.string().email("Adresse e-mail du client invalide"),
  amountTtc: z.number(),
  /**
   * Vrai tant que la facture est un brouillon.
   *
   * Décide de la phrase montrée au patron, et rien d'autre : c'est la base
   * qui applique la transition, sous condition de statut. Un écran qui aurait
   * pris du retard sur l'état réel ferait donc au pire une promesse fausse,
   * jamais un changement faux.
   */
  isDraft: z.boolean().default(true),
  /** Mot d'accompagnement libre. Remplace la phrase par défaut. */
  message: z.string().trim().max(2000).optional(),
});

export const sendMaterialOrderInput = z.object({
  confirmationId: z.string().optional(),
  orderId: z.string().uuid("Identifiant de commande attendu"),
  /**
   * Comme pour le devis et la facture : ces champs ne servent pas à
   * l'envoi, la fonction relit tout depuis la base. Ils servent au résumé
   * montré au patron, et c'est pour ça qu'ils entrent dans l'empreinte
   * signée.
   */
  supplierName: z.string().trim().min(1),
  supplierEmail: z.string().email("Adresse e-mail du fournisseur invalide"),
  totalHt: z.number(),
  /** Mot d'accompagnement libre. Remplace la phrase par défaut. */
  message: z.string().trim().max(2000).optional(),
});

export const sendPaymentReminderInput = z.object({
  confirmationId: z.string().optional(),
  invoiceId: z.string().uuid("Identifiant de facture attendu"),
  /**
   * 1 préventive · 2 amicale · 3 ferme · 4 mise en demeure. Le ton monte, et
   * le niveau 4 engage juridiquement : il n'a rien d'une valeur par défaut.
   */
  level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  /** Montré au patron. Ce qu'il confirme, c'est cette phrase-là. */
  invoiceNumber: z.string().trim().min(1),
  recipient: z.string().trim().min(1),
  amountDue: z.number(),
});

/**
 * Relancer un devis resté sans réponse.
 *
 * **Ce n'est pas le renvoyer.** Le lot 1 du plan UI/UX avait mis « Renvoyer »
 * sur le tableau de bord, faute de mieux, et l'avait écrit : « le libellé
 * changera avec le verbe ». Le voici. Le courrier ne répète pas « vous trouverez
 * ci-joint notre devis » à un client qui l'a déjà : il demande s'il a pu le
 * consulter, rappelle jusqu'à quand le prix tient, et rejoint la pièce pour
 * qu'il n'ait pas à la chercher. Le devis ne change ni de statut ni de date
 * d'envoi.
 */
export const followUpQuoteInput = z.object({
  confirmationId: z.string().optional(),
  quoteId: z.string().uuid("Identifiant de devis attendu"),
  quoteNumber: z.string().trim().min(1),
  recipient: z.string().trim().min(1),
  recipientEmail: z.string().email("Adresse e-mail du client invalide"),
  amountTtc: z.number(),
  /**
   * La date jusqu'à laquelle le prix tient, AAAA-MM-JJ. Facultative : le
   * tableau de bord ne la connaît pas, la file du soir si. Quand elle est là,
   * elle entre dans le résumé — c'est l'argument de la relance.
   */
  validUntil: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullish()
    .transform((v) => v ?? null),
  message: z.string().trim().max(2000).optional(),
});

/**
 * Établir une situation de travaux sur un devis accepté.
 *
 * N2 : la facture naît en brouillon, rien ne sort. Le pourcentage est celui
 * **de cette situation** — l'avancement non encore facturé —, et non un
 * avancement cumulé : c'est la convention de `create-invoice-from-quote`.
 */
export const establishSituationInput = z.object({
  confirmationId: z.string().optional(),
  quoteId: z.string().uuid("Identifiant de devis attendu"),
  percent: z
    .number({ message: "Un pourcentage est attendu" })
    .positive("Une situation porte sur un avancement positif")
    .max(100, "Une situation ne dépasse pas 100 % du marché"),
});

/**
 * Facturer un devis accepté et envoyer la facture, d'un seul accord.
 *
 * Le plan UI/UX : « créer la facture finale et l'envoyer sont deux gestes
 * séparés par une navigation. Le patron qui facture ne veut presque jamais
 * s'arrêter entre les deux. » Une seule confirmation annonce les deux — le
 * montant, le destinataire, et ce qui part.
 *
 * `amountHt` et `amountTtc` sont **ce que la facture réclamera** : le solde
 * après acomptes et situations, ou le montant de la situation. Ils entrent
 * dans l'empreinte, et la facture créée leur est comparée avant l'envoi.
 */
export const billAndSendInput = z
  .object({
    confirmationId: z.string().optional(),
    quoteId: z.string().uuid("Identifiant de devis attendu"),
    type: z.enum(["invoice", "situation"]),
    percent: z.number().positive().max(100).optional(),
    quoteNumber: z.string().trim().min(1),
    recipient: z.string().trim().min(1),
    recipientEmail: z.string().email("Adresse e-mail du client invalide"),
    amountHt: z.number(),
    amountTtc: z.number(),
  })
  .refine((i) => i.type !== "situation" || i.percent != null, {
    message: "Une situation porte un pourcentage",
    path: ["percent"],
  });

export type FollowUpQuoteInput = z.infer<typeof followUpQuoteInput>;
export type EstablishSituationInput = z.infer<typeof establishSituationInput>;
export type BillAndSendInput = z.infer<typeof billAndSendInput>;

export type SendQuoteInput = z.infer<typeof sendQuoteInput>;
export type SendInvoiceInput = z.infer<typeof sendInvoiceInput>;
export type SendMaterialOrderInput = z.infer<typeof sendMaterialOrderInput>;
export type GenerateDocumentPdfInput = z.infer<typeof generateDocumentPdfInput>;
export type GenerateFacturxInput = z.infer<typeof generateFacturxInput>;
export type SubmitChorusProInput = z.infer<typeof submitChorusProInput>;
export type SendPaymentReminderInput = z.infer<typeof sendPaymentReminderInput>;

// ─── Sorties ───────────────────────────────────────────────────────────────

export interface GeneratedPdf {
  url: string;
  path: string;
}

export interface GeneratedFacturx {
  xmlUrl: string | null;
  xmlLength: number;
}

export interface ChorusProReceipt {
  status: string;
  requestId: string | null;
}

/**
 * Ce que rapporte un envoi de document au client.
 *
 * Une seule forme pour le devis et pour la facture, parce que les deux
 * répondent aux mêmes questions et écrivent dans la même table. Deux
 * interfaces aux champs identiques auraient divergé au premier champ ajouté
 * d'un seul côté — c'est le raisonnement qui a fait de `document_sends` une
 * table générique, appliqué au type qui la reflète.
 */
export interface DocumentSendReceipt {
  sent: boolean;
  /**
   * Faux quand le courrier est parti mais que l'historique n'a pas pu
   * s'écrire. Le distinguer d'un échec d'envoi évite les deux mensonges
   * possibles : annoncer un raté alors que le client a reçu le document, ou
   * annoncer un succès propre alors qu'il n'en reste aucune trace.
   */
  recorded: boolean;
  recipient: string;
  subject: string;
}

export type QuoteSendReceipt = DocumentSendReceipt;
export type InvoiceSendReceipt = DocumentSendReceipt;

/** Une facture tout juste créée depuis un devis, telle que la base l'a écrite. */
export interface CreatedInvoice {
  id: string;
  invoiceNumber: string;
  totalHt: number;
  totalTtc: number;
  /** Figés par le trigger `fill_invoice_links`. Zéro hors facture de solde. */
  priorInvoicedHt: number;
  priorInvoicedTtc: number;
}

/** Facturé et envoyé : la facture, et ce que l'envoi a rapporté. */
export interface BilledAndSent extends DocumentSendReceipt {
  invoiceId: string;
  invoiceNumber: string;
}

export interface ReminderReceipt {
  sent: boolean;
  level: number;
  reason?: string;
}

// ─── Port ──────────────────────────────────────────────────────────────────

/**
 * Les traitements documentaires, décrits par ce qu'ils produisent.
 *
 * Aucune mention de HTTP, de Supabase ni d'Edge Function : le jour où la
 * génération de PDF part sur un service dédié, c'est cette implémentation qui
 * change, pas les services ni leurs tests.
 */
export interface DocumentPort {
  generatePdf(
    ctx: ExecutionContext,
    input: { kind: "quote" | "invoice"; id: string },
  ): Promise<GeneratedPdf>;

  generateFacturx(
    ctx: ExecutionContext,
    input: { invoiceId: string; profile?: string },
  ): Promise<GeneratedFacturx>;

  submitChorusPro(
    ctx: ExecutionContext,
    input: { invoiceId: string },
  ): Promise<ChorusProReceipt>;

  sendPaymentReminder(
    ctx: ExecutionContext,
    input: { invoiceId: string; level: number },
  ): Promise<ReminderReceipt>;

  sendQuote(
    ctx: ExecutionContext,
    input: { quoteId: string; message?: string },
  ): Promise<QuoteSendReceipt>;

  sendInvoice(
    ctx: ExecutionContext,
    input: { invoiceId: string; message?: string },
  ): Promise<InvoiceSendReceipt>;

  sendMaterialOrder(
    ctx: ExecutionContext,
    input: { orderId: string; message?: string },
  ): Promise<DocumentSendReceipt>;

  /** La relance d'un devis déjà envoyé : un autre courrier, le même devis. */
  followUpQuote(
    ctx: ExecutionContext,
    input: { quoteId: string; message?: string },
  ): Promise<DocumentSendReceipt>;

  createInvoiceFromQuote(
    ctx: ExecutionContext,
    input: { quoteId: string; type: "invoice" | "situation"; percent?: number },
  ): Promise<CreatedInvoice>;
}

// ─── Services ──────────────────────────────────────────────────────────────

const eur = (n: number) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);

const pourcentage = (n: number) =>
  `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(n)} %`;

/** « 2026-10-12 » → « 12 octobre 2026 ». Lu en UTC : une date n'a pas d'heure. */
const dateLongue = (iso: string) =>
  new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${iso}T00:00:00Z`));

export function createDocumentServices(port: DocumentPort) {
  const genererDocumentPdf = defineService<GenerateDocumentPdfInput, GeneratedPdf>({
    name: "generer_document_pdf",
    // Écrit un fichier dans le Storage de l'entreprise et rien d'autre.
    // Régénérer écrase : rien à rattraper.
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) =>
      port.generatePdf(ctx, { kind: input.kind, id: input.id }),
  });

  const genererFacturx = defineService<GenerateFacturxInput, GeneratedFacturx>({
    name: "generer_facturx",
    // Produit le XML CII EN 16931 à côté de la facture. Toujours interne :
    // c'est le dépôt qui sort de l'entreprise, pas la génération.
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) =>
      port.generateFacturx(ctx, {
        invoiceId: input.invoiceId,
        profile: input.profile,
      }),
  });

  const transmettreChorusPro = defineService<SubmitChorusProInput, ChorusProReceipt>({
    name: "transmettre_chorus_pro",
    // Dépôt sur le portail public de l'État. On ne retire pas une facture
    // déposée : c'est la définition même du niveau N3.
    level: ActionLevel.ExternalEffect,
    summarize: (i) =>
      `Déposer la facture ${i.invoiceNumber} sur Chorus Pro, à destination de ${i.recipient}. ` +
      `Le dépôt est définitif.`,
    handler: (ctx, input) =>
      port.submitChorusPro(ctx, { invoiceId: input.invoiceId }),
  });

  const relancerFacture = defineService<SendPaymentReminderInput, ReminderReceipt>({
    name: "relancer_facture",
    // Un e-mail part chez le client. Le niveau 4 est une mise en demeure :
    // elle produit des effets juridiques, et elle ne se rappelle pas.
    level: ActionLevel.ExternalEffect,
    summarize: (i) => {
      const ton = [
        "rappel avant échéance",
        "relance amicale",
        "relance ferme",
        "mise en demeure",
      ][i.level - 1];
      return (
        `Envoyer une ${ton} à ${i.recipient} pour la facture ${i.invoiceNumber}, ` +
        `${eur(i.amountDue)} restant dû.`
      );
    },
    handler: (ctx, input) =>
      port.sendPaymentReminder(ctx, {
        invoiceId: input.invoiceId,
        level: input.level,
      }),
  });

  const envoyerDevis = defineService<SendQuoteInput, QuoteSendReceipt>({
    name: "envoyer_devis",
    // Le devis part chez le client et l'engage sur un prix. Rien ne se
    // rattrape après : ni le courrier, ni l'impression qu'il laisse.
    level: ActionLevel.ExternalEffect,
    summarize: (i) =>
      `Envoyer le devis ${i.quoteNumber} (${eur(i.amountTtc)} TTC) à ${i.recipient} ` +
      `— ${i.recipientEmail}. Le devis passera au statut « envoyé » et ne sera ` +
      `plus modifiable.`,
    handler: (ctx, input) =>
      port.sendQuote(ctx, {
        quoteId: input.quoteId,
        ...(input.message ? { message: input.message } : {}),
      }),
  });

  const envoyerFacture = defineService<SendInvoiceInput, InvoiceSendReceipt>({
    name: "envoyer_facture",
    // Une facture part chez le client et lui réclame de l'argent. Elle ne se
    // rappelle pas — et l'envoyer démarre l'horloge du recouvrement.
    level: ActionLevel.ExternalEffect,
    summarize: (i) => {
      const entete =
        `Envoyer la facture ${i.invoiceNumber} (${eur(i.amountTtc)} TTC) à ` +
        `${i.recipient} — ${i.recipientEmail}.`;

      // La conséquence n'est pas la même selon l'état de départ, et c'est
      // toute la question posée au patron. Un premier envoi fait entrer la
      // facture dans le circuit de recouvrement : passé l'échéance, elle
      // deviendra impayée toute seule, puis recevra des relances de ton
      // croissant jusqu'à la mise en demeure — sans qu'on redemande rien.
      // Renvoyer une copie ne change rien à cet état ; annoncer les deux de
      // la même façon ferait confirmer un enclenchement là où il n'y en a pas.
      return i.isDraft
        ? `${entete} Elle passera au statut « envoyée » et entrera dans le ` +
            `circuit de relance : échéance dépassée, elle sera relancée ` +
            `automatiquement.`
        : `${entete} C'est un renvoi : son statut, son solde et sa ` +
            `progression de relance restent inchangés.`;
    },
    handler: (ctx, input) =>
      port.sendInvoice(ctx, {
        invoiceId: input.invoiceId,
        ...(input.message ? { message: input.message } : {}),
      }),
  });

  /**
   * Envoyer le bon de commande à un fournisseur.
   *
   * N3, comme envoyer un devis : c'est un engagement pris envers un tiers,
   * hors de l'entreprise, et qui ne se rappelle pas. `passerCommande`
   * (domaine `purchaseOrders`) ne fait que créer la commande en interne —
   * cette action-ci est celle qui la met dans les mains du fournisseur, et
   * c'est elle qui fait passer la commande de « à commander » à « commandée ».
   */
  const envoyerCommande = defineService<SendMaterialOrderInput, DocumentSendReceipt>({
    name: "envoyer_commande_materiaux",
    level: ActionLevel.ExternalEffect,
    summarize: (i) =>
      `Envoyer le bon de commande (${eur(i.totalHt)} HT) à ${i.supplierName} — ` +
      `${i.supplierEmail}. La commande passera au statut « commandée ».`,
    handler: (ctx, input) =>
      port.sendMaterialOrder(ctx, {
        orderId: input.orderId,
        ...(input.message ? { message: input.message } : {}),
      }),
  });

  const relancerDevis = defineService<FollowUpQuoteInput, DocumentSendReceipt>({
    name: "relancer_devis",
    // Un courrier part chez le client. Il n'engage pas davantage que le devis
    // déjà envoyé, mais il ne se rappelle pas, et un client relancé deux fois
    // la même semaine se souvient du ton plus que du prix.
    level: ActionLevel.ExternalEffect,
    summarize: (i) =>
      `Relancer ${i.recipient} — ${i.recipientEmail} — pour le devis ${i.quoteNumber} ` +
      `(${eur(i.amountTtc)} TTC)` +
      (i.validUntil ? `, valable jusqu'au ${dateLongue(i.validUntil)}` : "") +
      `. Un courrier de relance part, le devis joint ; le devis lui-même ne change pas.`,
    handler: (ctx, input) =>
      port.followUpQuote(ctx, {
        quoteId: input.quoteId,
        ...(input.message ? { message: input.message } : {}),
      }),
  });

  /**
   * Réservé à l'encadrement, comme les autres écritures qui engagent de
   * l'argent : une situation est une facture, même en brouillon.
   */
  const etablirSituation = defineService<EstablishSituationInput, CreatedInvoice>({
    name: "etablir_situation",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) =>
      port.createInvoiceFromQuote(ctx, {
        quoteId: input.quoteId,
        type: "situation",
        percent: input.percent,
      }),
  });

  const facturerEtEnvoyer = defineService<BillAndSendInput, BilledAndSent>({
    name: "facturer_et_envoyer",
    level: ActionLevel.ExternalEffect,
    summarize: (i) => {
      const quoi =
        i.type === "situation"
          ? `Établir une situation de ${pourcentage(i.percent ?? 0)} sur le devis ${i.quoteNumber}, ` +
            `soit ${eur(i.amountTtc)} TTC,`
          : `Établir la facture de solde du devis ${i.quoteNumber}, soit ${eur(i.amountTtc)} TTC ` +
            `restant dû après acomptes et situations,`;
      return (
        `${quoi} et l'envoyer à ${i.recipient} — ${i.recipientEmail}. ` +
        `Elle entrera dans le circuit de relance : échéance dépassée, elle sera ` +
        `relancée automatiquement.`
      );
    },
    handler: async (ctx, input) => {
      const facture = await port.createInvoiceFromQuote(ctx, {
        quoteId: input.quoteId,
        type: input.type,
        ...(input.percent != null ? { percent: input.percent } : {}),
      });

      // Ce qui a été annoncé contre ce qui a été créé. Entre la préparation et
      // le oui, un acompte a pu être émis depuis un autre écran : le solde
      // réclamerait alors moins que ce que le patron a lu. On n'envoie pas une
      // facture dont le montant n'est pas celui qu'il a confirmé.
      const netHt = round2(
        facture.totalHt - (input.type === "invoice" ? facture.priorInvoicedHt : 0),
      );
      if (Math.abs(netHt - input.amountHt) > 0.01) {
        throw new DomainError(
          "conflict",
          `La facture ${facture.invoiceNumber} a été créée pour ${eur(netHt)} HT, et non ` +
            `${eur(input.amountHt)} HT comme annoncé : elle n'est pas partie. Un acompte ou ` +
            `une situation a sans doute été émis entre-temps. Elle vous attend en brouillon.`,
          { invoiceId: facture.id, announcedHt: input.amountHt, createdHt: netHt },
        );
      }

      try {
        // `send-invoice` joint le PDF et refuse de partir sans lui.
        await port.generatePdf(ctx, { kind: "invoice", id: facture.id });
        const envoi = await port.sendInvoice(ctx, { invoiceId: facture.id });
        return { ...envoi, invoiceId: facture.id, invoiceNumber: facture.invoiceNumber };
      } catch (e) {
        // La confirmation est déjà consommée : rejouer créerait une seconde
        // facture. Dire où est la première est la seule suite utile.
        const cause = e instanceof Error ? e.message : "erreur inconnue";
        throw new DomainError(
          "conflict",
          `La facture ${facture.invoiceNumber} est créée mais n'est pas partie : ${cause} ` +
            `Elle vous attend en brouillon, envoyez-la depuis sa fiche.`,
          { invoiceId: facture.id },
        );
      }
    },
  });

  return {
    genererDocumentPdf,
    genererFacturx,
    transmettreChorusPro,
    relancerFacture,
    envoyerDevis,
    envoyerFacture,
    envoyerCommande,
    relancerDevis,
    etablirSituation,
    facturerEtEnvoyer,
  };
}

export type DocumentServices = ReturnType<typeof createDocumentServices>;
